import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  if (a === 0 && b === 0) return 1;
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a === 0 ? 1 : a;
};

const simplifyFraction = (num, den) => {
  const g = gcd(num, den);
  return {
    numerator: num / g,
    denominator: den / g,
  };
};

const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

const improperToMixedLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";

  const sign = numerator < 0 ? -1 : 1;
  const absNum = Math.abs(numerator);

  if (absNum < denominator) {
    return fractionToLatex({ numerator, denominator });
  }

  const whole = Math.floor(absNum / denominator);
  const rem = absNum % denominator;

  if (rem === 0) {
    return `${sign * whole}`;
  }

  const fracPart = `\\dfrac{${rem}}{${denominator}}`;
  if (sign === -1) return `-${whole}\\;${fracPart}`;
  return `${whole}\\;${fracPart}`;
};

const isValidFractionObject = (f) =>
  f &&
  typeof f === "object" &&
  f.numerator !== undefined &&
  f.denominator !== undefined;

const areFractionsEqual = (a, b) => {
  if (!isValidFractionObject(a) || !isValidFractionObject(b)) return false;

  const an = Number(a.numerator);
  const ad = Number(a.denominator);
  const bn = Number(b.numerator);
  const bd = Number(b.denominator);

  if (!isFinite(an) || !isFinite(ad) || !isFinite(bn) || !isFinite(bd)) {
    return false;
  }
  if (ad === 0 || bd === 0) return false;

  return an * bd === bn * ad;
};

/** parsowanie ułamka zwykłego / mieszanego z stringa */
const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    // sam ułamek "3/5" albo liczba całkowita "4"
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else {
      return null;
    }
  }

  return simplifyFraction(num, den);
};

/** parsowanie liczby dziesiętnej (kropka albo przecinek) */
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

/* ======================= GENERATOR ======================= */
/**
 * Zadanie: cenę pewnego towaru podniesiono/obniżono o p% – oblicz nową cenę.
 *
 * Jeden prosty typ:
 *  - procent: wielokrotność 5 (5%, 10%, 15%, ..., 50%)
 *  - cena początkowa i nowa cena są liczbami całkowitymi (zł).
 */
export function generateTask() {
  // losujemy, czy podwyżka czy obniżka
  const isIncrease = Math.random() < 0.5;

  // procent – wielokrotność 5
  const percents = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
  const percent = percents[randInt(0, percents.length - 1)];

  // chcemy, żeby nowa cena była całkowita:
  // newPrice = base * (100 ± p) / 100
  const factor = 100 + (isIncrease ? percent : -percent); // np. 120 lub 80
  const step = 100 / gcd(100, factor);
  const base = step * randInt(3, 10); // np. 60, 80, 100, 120...

  const factorNum = factor;
  const resultFraction = simplifyFraction(base * factorNum, 100);

  return {
    type: "PercentChangeSingleTask",
    data: {
      base,           // cena początkowa (zł)
      percent,        // o ile %
      isIncrease,     // true = podwyżka, false = obniżka
      resultFraction, // nowa cena (ułamek, zwykle całkowita)
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { base, percent, isIncrease } = data;
  const verb = isIncrease ? "podniesiono" : "obniżono";

  return (
    <p>
      Cenę pewnego towaru równą{" "}
      <InlineMath math={`${base}\\,\\text{zł}`} /> {verb} o{" "}
      <InlineMath math={`${percent}\\%`} />. Oblicz nową cenę tego towaru.
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 180, 45/2, 22 1/2"
        label=""
        suffixLatex="\text{zł}"
      />
    </div>
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.resultFraction;
  const expectedValue = expected.numerator / expected.denominator;

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return {
      isCorrect: false,
      expected,
      actual: null,
      comment: null,
    };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) najpierw próbujemy jako ułamek zwykły / mieszany
  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac && areFractionsEqual(expected, parsedFrac)) {
    return {
      isCorrect: true,
      expected,
      actual: parsedFrac,
      comment: null,
    };
  }

  // 2) potem jako liczba dziesiętna
  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const approxNum = Math.round(decimalVal * 100);
    const approxDen = 100;
    const approxFrac = simplifyFraction(approxNum, approxDen);

    const sameFraction = areFractionsEqual(expected, approxFrac);
    const closeEnough = Math.abs(decimalVal - expectedValue) <= 0.005; // ≈ do setnych

    const isCorrect = sameFraction || closeEnough;

    return {
      isCorrect,
      expected,
      actual: approxFrac,
      comment: null,
    };
  }

  return {
    isCorrect: false,
    expected,
    actual: null,
    comment: null,
  };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const { resultFraction } = data;
  const latex = improperToMixedLatex(resultFraction);

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${latex}\\,\\text{zł}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.raw ||
    !String(userAnswer.raw).trim()
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const raw = String(userAnswer.raw).trim();

  // spróbuj jako ułamek
  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac) {
    const latex = fractionToLatex(parsedFrac);
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={`${latex}\\,\\text{zł}`} />
      </div>
    );
  }

  // spróbuj jako dziesiętna
  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const normalized = raw.replace(",", ".");
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={`${normalized}\\,\\text{zł}`} />
      </div>
    );
  }

  // fallback – pokaż, co wpisał user, jako TeX
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${raw}\\,\\text{zł}`} />
    </div>
  );
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const { base, percent, isIncrease, resultFraction } = data;

  // kwota zmiany (powinna wyjść całkowita przy naszym generatorze)
  const change = (base * percent) / 100;

  const opSign = isIncrease ? "+" : "-";
  const verbSentence = isIncrease
    ? "Dodajemy tę kwotę do ceny początkowej."
    : "Odejmujemy tę kwotę od ceny początkowej.";

  const step1 = `${base} \\cdot \\dfrac{${percent}}{100} = ${change}\\,\\text{zł}`;
  const finalLatex = improperToMixedLatex(resultFraction);
  const step2 = `${base}\\,\\text{zł} ${opSign} ${change}\\,\\text{zł} = ${finalLatex}\\,\\text{zł}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Oblicz, ile wynosi{" "}
        <InlineMath math={`${percent}\\%`} /> ceny początkowej.
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> {verbSentence}
      </p>
      <BlockMath math={step2} />
    </div>
  );
}
